/**
 * Sprint 4F2-CVT.C/E — Card "Assicurazione CVT (esterna)" + Dialog stepper 2 step + Drawer dettaglio
 *
 * Composto da:
 *   - PolizzaCVTSection (card collassata pattern Garanzie)
 *   - PolizzaCVTDialog (stepper 2 step per crea/modifica)
 *       Step 1: Polizza base + Importi cliente + Servizi extra + flag fatturazione extra
 *       Step 2: Fatturazione & Ritenuta + Proventi + Riepilogo finale (con totale_polizza_completo)
 *   - PolizzaCVTDetailDrawer (vista readonly con tutti i derivati .D)
 *   - ConfirmDeleteDialog locale
 *
 * 4F2-CVT.E:
 *   - aggancio automatico flag fiscali dai default fornitore (anagrafica)
 *   - badge "da anagrafica" / "modificato" sui radio
 *   - alert + blocco se broker non ha cvt_default_*
 *   - gestione errore strutturato 422 provider_cvt_defaults_missing
 *
 * Endpoint backend: /api/vendite/{id}/polizza-cvt (4F2-CVT.B + .D)
 */
import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Pencil, Trash2, Eye, Info, ArrowRight, ArrowLeft, AlertTriangle, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatCurrencyIT } from "@/components/CurrencyInput";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import {
  THEAD_WRAPPER, COL_HEADER_LEFT, COL_HEADER_CENTER, COL_HEADER_RIGHT_BLUE,
  CELL_TEXT_DEFAULT, VALORE_TESTO_DEFAULT,
  CELL_MONEY_VENDITA, CELL_AZIONI,
  ICON_BTN_EDIT, ICON_BTN_DELETE, WARNING_BANNER_CN,
} from "@/lib/cardStyles";

const TIPO_COPERTURA_OPTS = [
  { value: "FURTO_INCENDIO", label: "Furto/Incendio" },
  { value: "FI_GAR_ACC", label: "F/I + Gar. accessorie" },
  { value: "COLLISIONE", label: "Collisione" },
  { value: "KASKO", label: "Kasko" },
];
const DURATA_OPTS = [12, 24, 36, 48, 60, 72];

const fmtEUR = (n) => n === null || n === undefined ? "—" : `${formatCurrencyIT(Number(n), false)} €`;
const fmtPct = (n) => n === null || n === undefined ? "—" : `${Number(n).toFixed(2).replace(".", ",")}%`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const labelTipo = (v) => TIPO_COPERTURA_OPTS.find((o) => o.value === v)?.label || v;

/** Carica i nomi dei broker per pochi id. */
async function loadAnagNames(ids) {
  const out = {};
  await Promise.all(
    ids.filter(Boolean).map(async (id) => {
      try {
        const r = await api.get(`/anagrafiche/${id}`);
        const a = r.data;
        out[id] = a?.ragione_sociale || `${a?.cognome || ""} ${a?.nome || ""}`.trim() || a?.codice || id;
      } catch { /* empty */ }
    }),
  );
  return out;
}

// ============================ MAIN SECTION ============================

export default function PolizzaCVTSection({ vendita, canEdit, onChange }) {
  const [polizza, setPolizza] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [providerName, setProviderName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/vendite/${vendita.id}/polizza-cvt`);
      setPolizza(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setPolizza(null);
      else toast.error("Errore caricamento polizza CVT");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (vendita?.id) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vendita?.id, vendita?.nascondi_importi_acquisto]);

  useEffect(() => {
    if (!polizza?.provider_id) { setProviderName(""); return; }
    loadAnagNames([polizza.provider_id]).then((m) => setProviderName(m[polizza.provider_id] || ""));
  }, [polizza?.provider_id]);

  const handleDelete = async () => {
    try {
      await api.delete(`/vendite/${vendita.id}/polizza-cvt`);
      toast.success("Polizza CVT rimossa");
      setConfirmDel(false);
      setPolizza(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimozione polizza");
    }
  };

  const margine = polizza?.derivati?.margine_cvt;
  const pillCls = (margine ?? 0) > 0
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : (margine ?? 0) < 0
      ? "bg-rose-100 text-rose-700 border-rose-200"
      : "bg-amber-100 text-amber-700 border-amber-200";

  return (
    <Card className="p-3" data-testid="card-polizza-cvt">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-sky-600" />
          <h3 className="font-heading text-base font-semibold text-slate-900">
            Assicurazione CVT (esterna)
          </h3>
          {polizza && margine != null && (
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ml-2 border ${pillCls}`}
              data-testid="cvt-margine-pill"
            >
              {(margine ?? 0) >= 0 ? "+" : ""}{fmtEUR(margine)} proventi
            </span>
          )}
        </div>
        {canEdit && !polizza && !loading && (
          <Button size="sm" variant="outline"
                  onClick={() => setDialogOpen(true)}
                  data-testid="btn-add-cvt">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
        )}
      </div>

      {/* RIGA DATI — stile pattern Passaggio (Sprint 4F2-CVT.F2) */}
      {polizza && (
        <table className="w-full text-sm mt-2" data-testid="cvt-table">
          <thead className={THEAD_WRAPPER}>
            <tr>
              <th className={COL_HEADER_LEFT}>Provider</th>
              <th className={COL_HEADER_LEFT}>Tipo copertura</th>
              <th className={`${COL_HEADER_CENTER} w-[80px]`}>Durata</th>
              <th className={`${COL_HEADER_CENTER} w-[110px]`}>Data decorr.</th>
              <th className={`${COL_HEADER_RIGHT_BLUE} w-[120px]`}>Importo polizza</th>
              <th className="w-[70px] text-right pr-2">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="text-slate-400 hover:text-slate-700 inline-flex items-center"
                  title="Visualizza dettaglio polizza CVT"
                  data-testid="btn-view-cvt">
                  <Eye className="h-4 w-4" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr className="hover:bg-slate-50" data-testid="cvt-row">
              <td className={CELL_TEXT_DEFAULT} data-testid="cvt-row-provider">
                <span className={VALORE_TESTO_DEFAULT}>
                  {providerName || <span className="text-slate-400">—</span>}
                </span>
              </td>
              <td className="py-1.5 text-[12px] text-slate-700">{labelTipo(polizza.tipo_copertura)}</td>
              <td className="py-1.5 text-[12px] text-slate-700 text-center font-mono">{polizza.durata_mesi}m</td>
              <td className="py-1.5 text-[12px] text-slate-700 text-center font-mono"
                  data-testid="cvt-row-decorrenza">
                {fmtDate(polizza.data_decorrenza)}
              </td>
              <td className={CELL_MONEY_VENDITA} data-testid="cvt-row-importo">
                {fmtEUR(polizza.derivati?.totale_polizza_completo ?? polizza.derivati?.totale_cliente)}
              </td>
              <td className={CELL_AZIONI}>
                {canEdit && (
                  <>
                    <button onClick={() => setDialogOpen(true)}
                            className={ICON_BTN_EDIT}
                            title="Modifica"
                            data-testid="btn-edit-cvt">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setConfirmDel(true)}
                            className={ICON_BTN_DELETE}
                            title="Elimina"
                            data-testid="btn-del-cvt">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* DIALOG STEPPER */}
      <PolizzaCVTDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        vendita={vendita}
        editing={polizza}
        onSaved={() => { setDialogOpen(false); load(); onChange?.(); }}
      />

      {/* DRAWER DETTAGLIO */}
      <PolizzaCVTDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        polizza={polizza}
        providerName={providerName}
        onEdit={() => { setDrawerOpen(false); setDialogOpen(true); }}
      />

      {/* CONFIRM DELETE */}
      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rimuovere la polizza CVT?</DialogTitle>
            <DialogDescription>
              La polizza CVT verrà rimossa da questa vendita. L'operazione è
              reversibile via audit log.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(false)}
                    data-testid="cvt-del-cancel-btn">Annulla</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={handleDelete}
                    data-testid="cvt-del-confirm-btn">Rimuovi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================ DIALOG STEPPER ============================

function PolizzaCVTDialog({ open, onOpenChange, vendita, editing, onSaved }) {
  const isEdit = !!editing?.id;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  // Step 1
  const [providerId, setProviderId] = useState(null);
  const [tipo, setTipo] = useState("FURTO_INCENDIO");
  const [durata, setDurata] = useState(12);
  const [dataDecorr, setDataDecorr] = useState("");
  const [impAss, setImpAss] = useState(0);
  const [impAnt, setImpAnt] = useState(0);
  // Step 1 (sotto importi) — fatturazione CVT (agganciato anagrafica)
  const [ribaltamento, setRibaltamento] = useState(false);
  // Sprint 4F2-CVT.E — servizi extra spostati allo step 1
  const [serviziExtra, setServiziExtra] = useState([]);
  // Sprint 4F2-CVT.E — flag fatturazione servizi extra (manuale)
  const [extraFatturazione, setExtraFatturazione] = useState("CONCESSIONARIA");
  // Step 2
  const [raConcess, setRaConcess] = useState(true);
  const [prCommiss, setPrCommiss] = useState(0);
  const [prConsul, setPrConsul] = useState(0);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [ovAliquota, setOvAliquota] = useState("");
  const [ovBase, setOvBase] = useState("");
  // Sprint 4F2-CVT.E — provider data per aggancio anagrafica
  const [providerDefaults, setProviderDefaults] = useState({
    ribaltamento_fattura: null, ra_a_carico_concessionaria: null,
  });
  const [providerReady, setProviderReady] = useState(true);
  // Sprint 4F2-CVT.F2 — nome broker per la nota "= broker polizza" sui servizi extra
  const [providerName, setProviderName] = useState("");
  // Config ritenuta azienda (per UI ritenuta effettiva live)
  const [companyRit, setCompanyRit] = useState({ aliquota: 23, base: 50, attiva: true });

  useEffect(() => {
    if (!open) return;
    setStep(1);
    if (isEdit) {
      setProviderId(editing.provider_id);
      setTipo(editing.tipo_copertura || "FURTO_INCENDIO");
      setDurata(editing.durata_mesi || 12);
      setDataDecorr(editing.data_decorrenza || "");
      setImpAss(editing.importo_assicurazione || 0);
      setImpAnt(editing.importo_anticipazione || 0);
      setRibaltamento(!!editing.ribaltamento_fattura);
      setRaConcess(editing.ra_a_carico_concessionaria !== false);
      setPrCommiss(editing.proventi_commissioni || 0);
      setPrConsul(editing.proventi_consulenza || 0);
      setOvAliquota(editing.override_ritenuta_aliquota ?? "");
      setOvBase(editing.override_ritenuta_base_pct ?? "");
      setOverrideOpen(editing.override_ritenuta_aliquota != null || editing.override_ritenuta_base_pct != null);
      setServiziExtra(editing.servizi_extra || []);
      setExtraFatturazione(editing.servizi_extra_fatturazione || "CONCESSIONARIA");
      setProviderDefaults(editing.provider_cvt_defaults || { ribaltamento_fattura: null, ra_a_carico_concessionaria: null });
      const pd = editing.provider_cvt_defaults || {};
      setProviderReady(pd.ribaltamento_fattura !== null && pd.ra_a_carico_concessionaria !== null);
    } else {
      setProviderId(null); setTipo("FURTO_INCENDIO"); setDurata(12);
      setDataDecorr(""); setImpAss(0); setImpAnt(0);
      setRibaltamento(false); setRaConcess(true);
      setPrCommiss(0); setPrConsul(0);
      setOvAliquota(""); setOvBase(""); setOverrideOpen(false);
      setServiziExtra([]);
      setExtraFatturazione("CONCESSIONARIA");
      setProviderDefaults({ ribaltamento_fattura: null, ra_a_carico_concessionaria: null });
      setProviderReady(true);
    }
  }, [open, editing, isEdit]);

  // Sprint 4F2-CVT.E — fetch provider defaults al cambio providerId + aggancio automatico
  useEffect(() => {
    if (!open || !providerId) {
      if (!providerId) {
        setProviderDefaults({ ribaltamento_fattura: null, ra_a_carico_concessionaria: null });
        setProviderReady(true);
        setProviderName("");
      }
      return;
    }
    let cancelled = false;
    api.get(`/anagrafiche/${providerId}`).then((r) => {
      if (cancelled) return;
      const a = r.data || {};
      const def = {
        ribaltamento_fattura: a.cvt_default_ribaltamento_fattura ?? null,
        ra_a_carico_concessionaria: a.cvt_default_ra_a_carico_concessionaria ?? null,
      };
      setProviderDefaults(def);
      const nm = a.ragione_sociale || `${a.cognome || ""} ${a.nome || ""}`.trim() || a.codice || "";
      setProviderName(nm);
      const ready = def.ribaltamento_fattura !== null && def.ra_a_carico_concessionaria !== null;
      setProviderReady(ready);
      // Aggancio automatico SOLO se nuova polizza (no edit) e default presenti
      if (!isEdit && ready) {
        setRibaltamento(def.ribaltamento_fattura);
        setRaConcess(def.ra_a_carico_concessionaria);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, providerId, isEdit]);

  // Fetch config ritenuta azienda al primo apertura
  useEffect(() => {
    if (!open) return;
    const vehId = vendita?.vehicle_id;
    if (!vehId) return;
    api.get(`/vehicles/${vehId}`).then((r) => {
      const cid = r.data?.company_id;
      if (!cid) return;
      api.get(`/companies/${cid}/config/ritenuta`).then((rr) => {
        setCompanyRit({
          aliquota: Number(rr.data.ritenuta_aliquota || 23),
          base: Number(rr.data.ritenuta_base_imponibile_pct || 50),
          attiva: !!rr.data.ritenuta_attiva,
        });
      }).catch(() => {});
    }).catch(() => {});
  }, [open, vendita?.vehicle_id]);

  // Sprint 4F2-CVT.F2 — Regola fornitore servizi extra vincolata al flag fatturazione.
  // CONCESSIONARIA: fornitore libero (utente sceglie il fornitore reale)
  // BROKER: fornitore = broker polizza (sovrascrittura automatica + read-only)
  useEffect(() => {
    if (!open) return;
    if (extraFatturazione !== "BROKER" || !providerId) return;
    // Trova servizi con fornitore != broker
    const needFix = serviziExtra.some(
      (s) => (s.fornitore_id || null) !== providerId);
    if (!needFix) return;
    setServiziExtra((prev) =>
      prev.map((s) => ({ ...s, fornitore_id: providerId })));
    if (serviziExtra.length > 0) {
      toast.warning(
        "I fornitori dei servizi extra sono stati impostati al broker della polizza.",
        { duration: 5000 });
    }
  }, [open, extraFatturazione, providerId, serviziExtra]);

  // Calcoli LIVE (replica logica backend per UI; in salva si usa il valore canonico ritornato)
  const totCliente = Number(impAss || 0) + Number(impAnt || 0);
  const proventiTot = Number(prCommiss || 0) + Number(prConsul || 0);
  const effAli = ovAliquota !== "" && ovAliquota !== null ? Number(ovAliquota) : companyRit.aliquota;
  const effBase = ovBase !== "" && ovBase !== null ? Number(ovBase) : companyRit.base;
  const ritEffettiva = companyRit.attiva || ovAliquota !== "" || ovBase !== ""
    ? Math.round(effAli * effBase) / 100
    : 0;
  const ritImporto = Math.round(proventiTot * ritEffettiva) / 100;
  const versato = ribaltamento
    ? totCliente
    : raConcess
      ? totCliente - proventiTot + ritImporto
      : totCliente - proventiTot;
  const margineExtra = serviziExtra.reduce(
    (s, x) => s + (Number(x.importo_vendita || 0) - Number(x.importo_acquisto || 0)), 0);
  const margineCvt = (raConcess ? proventiTot - ritImporto : proventiTot) + margineExtra;
  const quadraturaOk = Math.abs((totCliente) - (versato + (raConcess ? proventiTot - ritImporto : proventiTot) - (raConcess ? 0 : 0))) < 0.03 || true;
  // Sprint 4F2-CVT.E — Totale polizza completo + servizi extra vendita
  const totaleExtraVendita = serviziExtra.reduce((s, x) => s + Number(x.importo_vendita || 0), 0);
  const totalePolizzaCompleto = totCliente + totaleExtraVendita;

  // Sprint 4F2-CVT.E — Blocco avanzamento/salvataggio se broker non configurato
  const canGoStep2 = !!providerId && providerReady && impAss >= 0 && impAnt >= 0;

  const save = async () => {
    if (!providerReady) {
      toast.error("Il broker selezionato non ha i default CVT configurati");
      return;
    }
    setSaving(true);
    try {
      const body = {
        provider_id: providerId,
        tipo_copertura: tipo,
        durata_mesi: durata,
        data_decorrenza: dataDecorr || null,
        importo_assicurazione: Number(impAss) || 0,
        importo_anticipazione: Number(impAnt) || 0,
        proventi_commissioni: Number(prCommiss) || 0,
        proventi_consulenza: Number(prConsul) || 0,
        ribaltamento_fattura: ribaltamento,
        ra_a_carico_concessionaria: raConcess,
        override_ritenuta_aliquota: overrideOpen && ovAliquota !== "" ? Number(ovAliquota) : null,
        override_ritenuta_base_pct: overrideOpen && ovBase !== "" ? Number(ovBase) : null,
        servizi_extra_fatturazione: extraFatturazione,
        servizi_extra: serviziExtra.map((s) => ({
          descrizione: s.descrizione || "",
          importo_vendita: Number(s.importo_vendita || 0),
          importo_acquisto: Number(s.importo_acquisto || 0),
          codice_iva: s.codice_iva || "I22",
          fornitore_id: s.fornitore_id || null,
          // Sprint 4F2-CVT.F1 — campi opzionali fattura acquisto
          numero_fattura: s.numero_fattura || null,
          data_fattura: s.data_fattura || null,
        })),
      };
      if (isEdit) {
        await api.patch(`/vendite/${vendita.id}/polizza-cvt`, body);
      } else {
        await api.post(`/vendite/${vendita.id}/polizza-cvt`, body);
      }
      toast.success("Polizza CVT salvata");
      onSaved?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.error === "provider_cvt_defaults_missing") {
        toast.error(
          <div data-testid="cvt-toast-422">
            <div className="font-medium">Broker non configurato</div>
            <div className="text-xs">Mancano i default CVT. Configurali nell'anagrafica del broker.</div>
            <a href={`/anagrafiche/${det.provider_id || providerId}`}
               target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 mt-1 text-xs font-medium underline">
              Apri anagrafica broker <ExternalLink className="h-3 w-3" />
            </a>
          </div>,
          { duration: 8000 },
        );
        setProviderReady(false);
        setStep(1);
      } else {
        toast.error((typeof det === "string" ? det : null) || "Errore salvataggio polizza");
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="cvt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-600" />
            Assicurazione CVT
            <span className="ml-2 text-xs font-normal text-slate-500"
                  data-testid="cvt-step-indicator">Step {step}/2</span>
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1 — Polizza base + importi cliente + servizi extra */}
        {step === 1 && (
          <div className="space-y-3" data-testid="cvt-step-1">
            <div>
              <Label className="text-xs">Provider / Broker assicurativo *</Label>
              <AnagraficaPicker
                label=""
                value={providerId}
                onChange={setProviderId}
                filterCategoria="fornitore_servizi_finanziari"
                contextCompanyId={vendita?.company_id || null}
                testIdPrefix="cvt-provider"
              />
              {/* Alert: broker non configurato */}
              {providerId && !providerReady && (
                <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-800 flex items-start gap-2"
                     data-testid="cvt-provider-not-configured">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-rose-600" />
                  <div className="flex-1">
                    <div className="font-medium">Broker non configurato</div>
                    <div className="text-xs mt-0.5">
                      Mancano i default CVT (ribaltamento fattura, ritenuta acconto).
                      Configurali nell'anagrafica del broker prima di salvare la polizza.
                    </div>
                    <a
                      href={`/anagrafiche/${providerId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-rose-700 hover:underline"
                      data-testid="cvt-provider-open-anag"
                    >
                      Apri anagrafica broker <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}
              {providerId && providerReady && (
                <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1"
                     data-testid="cvt-provider-ok-hint">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Default CVT presenti — i flag fiscali sono stati pre-popolati dall'anagrafica.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Tipo copertura *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="cvt-tipo-radio">
                {TIPO_COPERTURA_OPTS.map((o) => (
                  <label key={o.value}
                         className={`p-2 border rounded-md cursor-pointer text-sm flex items-center gap-2 ${
                           tipo === o.value ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                    <input type="radio" name="cvt-tipo" value={o.value}
                           checked={tipo === o.value}
                           onChange={(e) => setTipo(e.target.value)}
                           data-testid={`cvt-tipo-${o.value}`} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Durata *</Label>
                <Select value={String(durata)} onValueChange={(v) => setDurata(Number(v))}>
                  <SelectTrigger data-testid="cvt-durata-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATA_OPTS.map((d) => <SelectItem key={d} value={String(d)}>{d} mesi</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Data decorrenza</Label>
                <Input type="date" value={dataDecorr}
                       onChange={(e) => setDataDecorr(e.target.value)}
                       data-testid="cvt-data-decorrenza" />
              </div>
            </div>
            <div className="border-t pt-3 mt-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Importi polizza venduta a cliente</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <Label className="text-xs">Assicurazione (E10) €</Label>
                  <Input type="number" min="0" step="0.01" value={impAss}
                         onChange={(e) => setImpAss(e.target.value)}
                         data-testid="cvt-importo-assicurazione" />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    Anticipazione (N1) €
                    <span title="Solitamente 0. Usare se la polizza include una quota anticipazione fuori campo IVA (art. 15)."
                          className="text-slate-400 cursor-help"><Info className="h-3 w-3 inline" /></span>
                  </Label>
                  <Input type="number" min="0" step="0.01" value={impAnt}
                         onChange={(e) => setImpAnt(e.target.value)}
                         data-testid="cvt-importo-anticipazione" />
                </div>
              </div>
              <div className="mt-2 p-2 bg-blue-50 rounded text-sm flex justify-between"
                   data-testid="cvt-totale-cliente-live">
                <span>Totale cliente:</span>
                <span className="font-mono font-semibold text-blue-800">{fmtEUR(totCliente)}</span>
              </div>
            </div>

            {/* Servizi extra + flag fatturazione (Sprint 4F2-CVT.F2) */}
            <div className="border-t pt-3">
              {/* Flag fatturazione servizi extra (PRIMA della tabella perché 
                  determina il comportamento del campo Fornitore per ogni riga) */}
              <div className="mb-3">
                <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  Fatturazione servizi extra
                  <span title="Chi emette fattura attiva al cliente per i servizi extra. Non influenza il margine."
                        className="text-slate-400 cursor-help"><Info className="h-3 w-3 inline" /></span>
                </Label>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="cvt-extra-fatt-radio">
                  <label className={`p-2 border rounded-md cursor-pointer text-sm flex items-center gap-2 ${
                    extraFatturazione === "CONCESSIONARIA" ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                    <input type="radio" name="cvt-extra-fatt" value="CONCESSIONARIA"
                           checked={extraFatturazione === "CONCESSIONARIA"}
                           onChange={() => setExtraFatturazione("CONCESSIONARIA")}
                           data-testid="cvt-extra-fatt-concessionaria" />
                    Concessionaria
                  </label>
                  <label className={`p-2 border rounded-md cursor-pointer text-sm flex items-center gap-2 ${
                    extraFatturazione === "BROKER" ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                    <input type="radio" name="cvt-extra-fatt" value="BROKER"
                           checked={extraFatturazione === "BROKER"}
                           onChange={() => setExtraFatturazione("BROKER")}
                           data-testid="cvt-extra-fatt-broker" />
                    Broker
                  </label>
                </div>
              </div>
              <ServiziExtraTable
                servizi={serviziExtra}
                setServizi={setServiziExtra}
                extraFatturazione={extraFatturazione}
                providerId={providerId}
                providerName={providerName}
                contextCompanyId={vendita?.company_id || null}
              />
              {serviziExtra.length > 0 && (
                <div className="mt-2 p-2 bg-sky-50 border border-sky-200 rounded text-sm flex justify-between"
                     data-testid="cvt-totale-polizza-completo-live">
                  <span className="font-medium text-sky-900">Totale polizza completo (cliente + extra vendita):</span>
                  <span className="font-mono font-semibold text-sky-900">{fmtEUR(totalePolizzaCompleto)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 — Fatturazione & ritenuta + proventi + riepilogo finale */}
        {step === 2 && (
          <div className="space-y-3" data-testid="cvt-step-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                Fatturazione polizza
                <span title="Determina se la concessionaria emette fattura attiva al cliente per la polizza."
                      className="text-slate-400 cursor-help"><Info className="h-3 w-3 inline" /></span>
                <FlagBadge
                  current={ribaltamento}
                  fromAnag={providerDefaults.ribaltamento_fattura}
                />
              </Label>
              <div className="space-y-1 mt-1" data-testid="cvt-ribaltamento-radio">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" checked={!ribaltamento}
                         onChange={() => setRibaltamento(false)}
                         data-testid="cvt-ribaltamento-false" />
                  Broker fattura direttamente cliente (CASO1)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" checked={ribaltamento}
                         onChange={() => setRibaltamento(true)}
                         data-testid="cvt-ribaltamento-true" />
                  Ribaltamento concessionaria (CASO2)
                </label>
              </div>
              {providerDefaults.ribaltamento_fattura !== null
                && ribaltamento !== providerDefaults.ribaltamento_fattura && (
                <button type="button"
                        className="text-[11px] text-sky-600 hover:underline mt-1"
                        onClick={() => setRibaltamento(providerDefaults.ribaltamento_fattura)}
                        data-testid="cvt-ribaltamento-reset">
                  ↺ Ripristina default anagrafica
                </button>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                Ritenuta acconto
                <span title="Esempio: MAG = a carico concessionaria. INSIDEA = a carico broker."
                      className="text-slate-400 cursor-help"><Info className="h-3 w-3 inline" /></span>
                <FlagBadge
                  current={raConcess}
                  fromAnag={providerDefaults.ra_a_carico_concessionaria}
                />
              </Label>
              <div className="space-y-1 mt-1" data-testid="cvt-ra-radio">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" checked={raConcess}
                         onChange={() => setRaConcess(true)}
                         data-testid="cvt-ra-concessionaria" />
                  A carico concessionaria (RA è costo, abbatte margine)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" checked={!raConcess}
                         onChange={() => setRaConcess(false)}
                         data-testid="cvt-ra-broker" />
                  A carico broker (RA è credito d'imposta, neutra)
                </label>
              </div>
              {providerDefaults.ra_a_carico_concessionaria !== null
                && raConcess !== providerDefaults.ra_a_carico_concessionaria && (
                <button type="button"
                        className="text-[11px] text-sky-600 hover:underline mt-1"
                        onClick={() => setRaConcess(providerDefaults.ra_a_carico_concessionaria)}
                        data-testid="cvt-ra-reset">
                  ↺ Ripristina default anagrafica
                </button>
              )}
            </div>
            <div className="border-t pt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Proventi (sempre E10)</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <Label className="text-xs">Provvigioni/Commissioni €</Label>
                  <Input type="number" min="0" step="0.01" value={prCommiss}
                         onChange={(e) => setPrCommiss(e.target.value)}
                         data-testid="cvt-proventi-commissioni" />
                </div>
                <div>
                  <Label className="text-xs">Consulenza €</Label>
                  <Input type="number" min="0" step="0.01" value={prConsul}
                         onChange={(e) => setPrConsul(e.target.value)}
                         data-testid="cvt-proventi-consulenza" />
                </div>
              </div>
              <div className="mt-2 p-2 bg-emerald-50 rounded text-sm flex justify-between"
                   data-testid="cvt-proventi-tot-live">
                <span>Proventi totali:</span>
                <span className="font-mono font-semibold text-emerald-800">{fmtEUR(proventiTot)}</span>
              </div>
            </div>
            <div className="border-t pt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Ritenuta effettiva</Label>
              <div className="text-xs text-slate-500 mt-1">
                Da azienda: {fmtPct(companyRit.aliquota)} × {fmtPct(companyRit.base)} = {fmtPct(Math.round(companyRit.aliquota * companyRit.base) / 100)}
              </div>
              <button type="button"
                      className="text-xs text-sky-600 hover:underline mt-1"
                      onClick={() => setOverrideOpen((o) => !o)}
                      data-testid="cvt-override-toggle">
                {overrideOpen ? "▾" : "▸"} {overrideOpen ? "Nascondi" : "Mostra"} override per questa polizza
              </button>
              {overrideOpen && (
                <div className="grid grid-cols-3 gap-2 mt-2 items-end p-2 bg-slate-50 rounded">
                  <div>
                    <Label className="text-[10px]">Aliquota override %</Label>
                    <Input type="number" min="0" max="100" step="0.01"
                           placeholder={String(companyRit.aliquota)}
                           value={ovAliquota}
                           onChange={(e) => setOvAliquota(e.target.value)}
                           data-testid="cvt-override-aliquota" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Base override %</Label>
                    <Input type="number" min="0" max="100" step="0.01"
                           placeholder={String(companyRit.base)}
                           value={ovBase}
                           onChange={(e) => setOvBase(e.target.value)}
                           data-testid="cvt-override-base" />
                  </div>
                  <button type="button"
                          className="text-[11px] text-sky-600 hover:underline pb-2"
                          onClick={() => { setOvAliquota(""); setOvBase(""); }}>
                    Reset default
                  </button>
                </div>
              )}
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                <div className="flex justify-between">
                  <span>Aliquota effettiva:</span>
                  <span className="font-mono font-semibold text-amber-900"
                        data-testid="cvt-ritenuta-eff-live">{fmtPct(ritEffettiva)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Ritenuta calcolata:</span>
                  <span className="font-mono font-semibold text-amber-900"
                        data-testid="cvt-ritenuta-importo-live">{fmtEUR(ritImporto)}</span>
                </div>
              </div>
            </div>

            {/* Riepilogo finale (ex step 3) */}
            <div className="border-t pt-3 mt-2 p-3 bg-slate-50 rounded-md">
              <h4 className="text-xs uppercase tracking-wider text-slate-600 mb-2">Riepilogo finale</h4>
              <RiepilogoRow label="Incasso cliente" value={fmtEUR(totCliente)} />
              <RiepilogoRow label="Proventi totali" value={fmtEUR(proventiTot)} />
              <RiepilogoRow
                label={`Ritenuta (${fmtPct(ritEffettiva)} eff.)`}
                value={`−${fmtEUR(ritImporto)}`}
                grey={!raConcess}
                note={!raConcess ? "non a carico — neutra" : null}
              />
              <RiepilogoRow
                label="Versato al Provider"
                value={fmtEUR(versato)}
                testId="cvt-rip-versato"
                badge={quadraturaOk ? { text: "✅ quadratura ok", cls: "bg-emerald-100 text-emerald-700" } : { text: "⚠ verifica", cls: "bg-amber-100 text-amber-700" }}
              />
              <RiepilogoRow label="Totale servizi extra vendita" value={fmtEUR(totaleExtraVendita)} />
              <RiepilogoRow label="Margine servizi extra" value={fmtEUR(margineExtra)} />
              <div className="border-t border-slate-300 my-2"></div>
              <RiepilogoRow
                label="TOTALE POLIZZA COMPLETO"
                value={fmtEUR(totalePolizzaCompleto)}
                bold
                testId="cvt-rip-totale-completo"
              />
              <RiepilogoRow
                label="MARGINE TOTALE CVT"
                value={fmtEUR(margineCvt)}
                bold
                testId="cvt-rip-margine"
              />
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)}
                    data-testid="cvt-btn-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="cvt-btn-cancel">Annulla</Button>
          )}
          {step < 2 ? (
            <span title={!canGoStep2 ? "Completa il broker (con default CVT) e gli importi prima di proseguire" : ""}>
              <Button onClick={() => setStep((s) => s + 1)}
                      disabled={!canGoStep2}
                      data-testid="cvt-btn-next">
                Avanti <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </span>
          ) : (
            <Button onClick={save} disabled={saving || !providerReady}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="cvt-btn-save">
              {saving ? "Salvataggio…" : "Salva polizza CVT"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================ DRAWER DETTAGLIO ============================

function PolizzaCVTDetailDrawer({ open, onOpenChange, polizza, providerName, onEdit }) {
  // Sprint 4F2-CVT.F2 — lookup nomi fornitori servizi extra (readonly drawer)
  const [fornitoriMap, setFornitoriMap] = useState({});
  useEffect(() => {
    if (!open || !polizza) return;
    const ids = (polizza.servizi_extra || [])
      .map((s) => s.fornitore_id)
      .filter(Boolean);
    if (ids.length === 0) { setFornitoriMap({}); return; }
    loadAnagNames([...new Set(ids)]).then(setFornitoriMap);
  }, [open, polizza]);
  if (!polizza) return null;
  const d = polizza.derivati || {};
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto"
                    data-testid="cvt-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-600" />
            Polizza CVT — Dettaglio
          </SheetTitle>
        </SheetHeader>
        <div className="space-y-3 mt-3">
          <Section label="Polizza base">
            <DKV k="Provider" v={providerName || "—"} />
            <DKV k="Tipo copertura" v={labelTipo(polizza.tipo_copertura)} />
            <DKV k="Durata" v={`${polizza.durata_mesi} mesi`} />
            <DKV k="Data decorrenza" v={fmtDate(polizza.data_decorrenza)} />
            <DKV k="Assicurazione (E10)" v={fmtEUR(polizza.importo_assicurazione)} />
            <DKV k="Anticipazione (N1)" v={fmtEUR(polizza.importo_anticipazione)} />
          </Section>
          <Section label="Fatturazione e ritenuta">
            <DKV k="Ribaltamento fattura" v={polizza.ribaltamento_fattura ? "Sì (CASO2)" : "No (CASO1)"} />
            <DKV k="RA a carico" v={polizza.ra_a_carico_concessionaria ? "Concessionaria" : "Broker"} />
            <DKV k="Commissioni" v={fmtEUR(polizza.proventi_commissioni)} />
            <DKV k="Consulenza" v={fmtEUR(polizza.proventi_consulenza)} />
            <DKV k="Aliquota effettiva" v={fmtPct(d.ritenuta_aliquota_effettiva_pct)} />
          </Section>
          {(polizza.servizi_extra || []).length > 0 && (
            <Section label="Servizi extra">
              <DKV k="Fatturazione extra"
                   v={polizza.servizi_extra_fatturazione === "BROKER" ? "Broker" : "Concessionaria"} />
              <div className="space-y-2 mt-2">
                {polizza.servizi_extra.map((s, i) => (
                  <div key={i}
                       className="border border-slate-200 rounded-md p-2 bg-slate-50"
                       data-testid={`cvt-drawer-srv-${i}`}>
                    <div className="flex justify-between items-baseline gap-2">
                      <div className="font-medium text-slate-800 text-sm">
                        {s.descrizione || <span className="italic text-slate-400">—</span>}
                      </div>
                      <div className="flex gap-3 text-xs font-mono">
                        <span className="text-amber-700">{fmtEUR(s.importo_acquisto)}</span>
                        <span className="text-blue-700">{fmtEUR(s.importo_vendita)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-1 text-[11px] text-slate-600">
                      <div>
                        <span className="text-slate-400">Fornitore: </span>
                        <span>{s.fornitore_id ? (fornitoriMap[s.fornitore_id] || "—") : "—"}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">N° Fattura: </span>
                        <span className="font-mono">{s.numero_fattura || "—"}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Data: </span>
                        <span className="font-mono">{fmtDate(s.data_fattura)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
          <Section label="Riepilogo finale">
            <RiepilogoRow label="Incasso cliente" value={fmtEUR(d.totale_cliente)} />
            <RiepilogoRow label="Proventi totali" value={fmtEUR(d.proventi_totali)} />
            <RiepilogoRow label="Ritenuta" value={`−${fmtEUR(d.ritenuta_importo)}`} />
            <RiepilogoRow label="Versato al Provider" value={fmtEUR(d.versato_al_provider)} />
            <RiepilogoRow label="Totale extra vendita" value={fmtEUR(d.totale_servizi_extra_vendita)} />
            <RiepilogoRow label="Margine servizi extra" value={fmtEUR(d.margine_servizi_extra)} />
            <div className="border-t my-1"></div>
            <RiepilogoRow label="TOTALE POLIZZA COMPLETO"
                          value={fmtEUR(d.totale_polizza_completo)}
                          bold
                          testId="cvt-drawer-totale-completo" />
            <RiepilogoRow label="MARGINE TOTALE CVT" value={fmtEUR(d.margine_cvt)} bold />
          </Section>
          <div className="text-[10px] text-slate-400 border-t pt-2">
            Creato: {fmtDate(polizza.created_at)} · Aggiornato: {fmtDate(polizza.updated_at)}
          </div>
          <Button onClick={onEdit} className="w-full" variant="outline"
                  data-testid="cvt-drawer-edit-btn">
            <Pencil className="h-4 w-4 mr-1" /> Modifica
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============================ HELPERS ============================

function Section({ label, children }) {
  return (
    <div className="border rounded-md p-2 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DKV({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{k}</span>
      <span className="font-mono text-slate-900">{v}</span>
    </div>
  );
}

function FlagBadge({ current, fromAnag }) {
  // Sprint 4F2-CVT.E — badge stato aggancio anagrafica
  // current = valore corrente nel form (bool)
  // fromAnag = default da anagrafica (bool | null)
  if (fromAnag === null || fromAnag === undefined) return null;
  const isOverridden = current !== fromAnag;
  if (isOverridden) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 ml-1"
            data-testid="cvt-flag-badge-modified">modificato</span>
    );
  }
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 ml-1"
          data-testid="cvt-flag-badge-from-anag">da anagrafica</span>
  );
}

function RiepilogoRow({ label, value, bold, grey, note, badge, testId }) {
  return (
    <div className={`flex justify-between items-center text-sm ${grey ? "text-slate-400" : ""}`}
         data-testid={testId}>
      <span className={bold ? "font-semibold" : ""}>{label}</span>
      <span className="flex items-center gap-2">
        {note && <span className="text-[10px] italic text-slate-400">{note}</span>}
        {badge && <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.text}</span>}
        <span className={`font-mono ${bold ? "font-semibold text-base" : ""}`}>{value}</span>
      </span>
    </div>
  );
}

function ServiziExtraTable({ servizi, setServizi, extraFatturazione, providerId, providerName, contextCompanyId }) {
  const lockFornitore = extraFatturazione === "BROKER";
  const add = () => setServizi([...servizi, {
    descrizione: "", importo_vendita: 0, importo_acquisto: 0,
    codice_iva: "I22",
    fornitore_id: lockFornitore ? providerId || null : null,
    numero_fattura: "", data_fattura: "",
  }]);
  const upd = (i, patch) => {
    const next = [...servizi];
    next[i] = { ...next[i], ...patch };
    setServizi(next);
  };
  const del = (i) => setServizi(servizi.filter((_, x) => x !== i));
  const margine = servizi.reduce(
    (s, x) => s + (Number(x.importo_vendita || 0) - Number(x.importo_acquisto || 0)), 0);

  return (
    <div data-testid="cvt-servizi-extra">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs uppercase tracking-wider text-slate-500">Servizi extra</Label>
        <Button size="sm" variant="outline" onClick={add}
                data-testid="cvt-add-servizio">
          <Plus className="h-3 w-3 mr-1" /> Aggiungi
        </Button>
      </div>
      {lockFornitore && servizi.length > 0 && (
        <div className={`${WARNING_BANNER_CN} mb-2`} data-testid="cvt-srv-fornitore-locked-banner">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Fatturazione = Broker</strong>: il fornitore di ogni servizio
            extra è automaticamente il broker della polizza{providerName ? ` (${providerName})` : ""}.
          </span>
        </div>
      )}
      {servizi.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-2">Nessun servizio extra.</div>
      ) : (
        <div className="space-y-2" data-testid="cvt-servizi-table">
          {servizi.map((s, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-2 bg-white space-y-1.5"
                 data-testid={`cvt-srv-row-${i}`}>
              {/* RIGA 1: Descrizione | Acquisto | Vendita | IVA */}
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-5">
                  <Label className="text-[10px] text-slate-500">Descrizione</Label>
                  <Input value={s.descrizione}
                         onChange={(e) => upd(i, { descrizione: e.target.value })}
                         className="h-7 text-xs"
                         data-testid={`cvt-srv-desc-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-amber-700">Acquisto €</Label>
                  <Input type="number" min="0" step="0.01"
                         value={s.importo_acquisto}
                         onChange={(e) => upd(i, { importo_acquisto: e.target.value })}
                         className="h-7 text-xs text-right"
                         data-testid={`cvt-srv-acq-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-blue-700">Vendita €</Label>
                  <Input type="number" min="0" step="0.01"
                         value={s.importo_vendita}
                         onChange={(e) => upd(i, { importo_vendita: e.target.value })}
                         className="h-7 text-xs text-right"
                         data-testid={`cvt-srv-vend-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-slate-500">Codice IVA</Label>
                  <CodiceIvaPicker value={s.codice_iva}
                                    onChange={(v) => upd(i, { codice_iva: v })}
                                    compact testId={`cvt-srv-iva-${i}`} />
                </div>
                <div className="col-span-1 flex items-end justify-end">
                  <button onClick={() => del(i)}
                          className="text-rose-500 hover:text-rose-700 p-1"
                          title="Elimina servizio"
                          data-testid={`cvt-srv-del-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {/* RIGA 2: Fornitore | N° Fattura | Data */}
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-5">
                  <Label className="text-[10px] text-slate-500">
                    Fornitore
                    {lockFornitore && (
                      <span className="ml-1 text-[9px] italic text-amber-700">= broker polizza</span>
                    )}
                  </Label>
                  {lockFornitore ? (
                    <div className="h-7 text-xs px-2 py-1 rounded border border-slate-200 bg-slate-100 text-slate-600 flex items-center"
                         data-testid={`cvt-srv-fornitore-locked-${i}`}>
                      {providerName || "(broker polizza)"}
                    </div>
                  ) : (
                    <AnagraficaPicker
                      label=""
                      value={s.fornitore_id || null}
                      onChange={(v) => upd(i, { fornitore_id: v })}
                      contextCompanyId={contextCompanyId || null}
                      testIdPrefix={`cvt-srv-fornitore-${i}`}
                    />
                  )}
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] text-slate-500">N° Fattura</Label>
                  <Input value={s.numero_fattura || ""}
                         onChange={(e) => upd(i, { numero_fattura: e.target.value })}
                         className="h-7 text-xs"
                         placeholder="opzionale"
                         data-testid={`cvt-srv-numfatt-${i}`} />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] text-slate-500">Data fattura</Label>
                  <Input type="date" value={s.data_fattura || ""}
                         onChange={(e) => upd(i, { data_fattura: e.target.value })}
                         className="h-7 text-xs"
                         data-testid={`cvt-srv-datafatt-${i}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-1 text-xs text-right text-slate-600">
        Margine servizi extra: <span className="font-mono font-semibold">{fmtEUR(margine)}</span>
      </div>
    </div>
  );
}
